import { Empty, Tag, Tooltip, DatePicker, Select } from "antd"; // Restored Select import
import React, { useMemo, useState, useEffect, useContext } from "react";
import { message } from "antd";
import dayjs from "dayjs";
import car from "../../../Assets/Vehicle Images/total vehicle.png";
import carOnline from "../../../Assets/Vehicle Images/Vehicle online.png";
import carOffline from "../../../Assets/Vehicle Images/vehicle offline.png";
import carSuspended from "../../../Assets/Vehicle Images/Vehicle shutdown.png";
import alarm from "../../../Assets/Icons/alarm (1) 1.svg";
import HighRiskComponent from "../../../Components/HighRiskComponent";
import "./style.css";
import courseUp from "../../../Assets/Icons/Course Up.svg";
import courseDown from "../../../Assets/Icons/Course Down.svg";
import customerLogo from "../../../Assets/Icons/customer manage.svg";
import HeatMapComponent from "../../../Components/HeatMapComponent";
import TimelineGraph from "./TimeLineGraph";
import StackedBarChart from "./StackedBarChart";
import { useNavigate } from "react-router-dom";
import {
  getDashboardData,
  getYearlyTheftStatus,
} from "../../../API/User/dashboard";
import { UserContext } from "../../../App";

export const TrendTag = ({ trend }) => (
  <Tag
    color={trend?.direction === "up" ? "green" : "red"}
    icon={
      <img
        src={trend?.direction === "up" ? courseUp : courseDown}
        alt={trend?.direction}
      />
    }
  >
    Last Week: {trend?.count}
  </Tag>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { unreadNotificationCount, setUnreadNotificationCount } = useContext(UserContext);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const [selectedYear, setSelectedYear] = useState(null);
  const [annualTheftData, setAnnualTheftData] = useState([]);
  const [annualTheftCount, setAnnualTheftCount] = useState(null);
  const [graphLoading, setGraphLoading] = useState(false);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await getDashboardData();
      if (Math.floor(response?.status / 100) === 2) {
        const data = response?.data?.data || {};
        setDashboardData(data);
        console.log("DATA", data)
        setUnreadNotificationCount(data?.unread_notification_count)
        setAnnualTheftCount(data?.annual_theft_count || 0);
      } else {
        messageApi.error("Unexpected response from server.");
      }
    } catch (error) {
      messageApi.error("Failed to fetch dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Handle year changes from DatePicker
  const handleYearChange = async (date) => {
    setSelectedYear(date);

    if (!date) {
      setAnnualTheftData([]);
      setAnnualTheftCount(dashboardData?.annual_theft_count || 0);
      return;
    }

    const yearStr = date.format("YYYY");

    try {
      setGraphLoading(true);
      const response = await getYearlyTheftStatus(yearStr);
      const responseData = response?.data?.data || response?.data || {};

      const details = responseData?.annual_theft_details || [];
      const count = responseData?.annual_theft_count ?? 0;

      setAnnualTheftData(details);
      setAnnualTheftCount(count);
    } catch (error) {
      messageApi.error("Error fetching yearly data.");
    } finally {
      setGraphLoading(false);
    }
  };

  const data = dashboardData?.automated_shutdown_events || [];
  const graphData = selectedYear
    ? annualTheftData
    : dashboardData?.annual_theft_details || [];

  const hasGraphData =
    Array.isArray(graphData) &&
    graphData.length > 0 &&
    graphData.some(
      (item) => item.confirmed > 0 || item.unverified > 0 || item.safe > 0,
    );

  const [selectedVehicles, setSelectedVehicles] = useState([]);

  const cleanVehicle = (vehicle) => {
    if (!vehicle) return "";
    return vehicle.toString().trim().toLowerCase();
  };

  const vehicleOptions = useMemo(() => {
    return Array.from(
      new Set(data.map((item) => cleanVehicle(item.vehicle))),
    ).map((vehicle) => ({
      label: vehicle.replace("-", " ").toUpperCase(),
      value: vehicle,
    }));
  }, [data]);

  const filteredData = useMemo(() => {
    if (!selectedVehicles.length) return data;
    return data.filter((item) =>
      selectedVehicles.includes(cleanVehicle(item.vehicle)),
    );
  }, [data, selectedVehicles]);

  const formattedTimelineData = filteredData
    .map((item, index) => ({
      ...item,
      time: `${new Date(item.time).toLocaleString()}_${index}`,
      displayTime: new Date(item.time).toLocaleString(),
      vehicle: cleanVehicle(item.vehicle),
    }))
    .sort((a, b) => new Date(a.time) - new Date(b.time));

  const getStrokeColor = (value) => {
    if (value < 100) return "#1FC16B";
    if (value >= 100 && value <= 159) return "#FFBF00";
    if (value >= 160) return "#D80000";
    return "#d9d9d9";
  };

  const handleCustomerClick = (customer) => {
    navigate("/calls");
  };

  const isCompressed = selectedVehicles.length > 5;
  useEffect(() => {
    if (vehicleOptions.length) {
      setSelectedVehicles(vehicleOptions.slice(0, 5).map((v) => v.value));
    }
  }, [vehicleOptions]);

  const heatmapData = useMemo(() => {
    return dashboardData?.theft_heatmap_points || [];
  }, [dashboardData]);

  return (
    <div className="dashboard">
      {contextHolder}
      <div className="row">
        <div className="tabs-container">
          <div className="tab-item tab-black">
            <div className="tab-title">Total Vehicles</div>
            <div className="tab-content">
              <div className="left">
                <div className="tab-value">
                  {dashboardData?.total_vehicles || 0}
                </div>
                <div className="tab-tag">
                  <TrendTag trend={dashboardData?.total_vehicles_change} />
                </div>
              </div>
              <div className="right">
                <img src={car} alt="car image" />
              </div>
            </div>
          </div>

          <HighRiskComponent
            high_risk_vehicles={dashboardData?.high_risk_vehicles}
            threshold={dashboardData?.threshold}
          />
        </div>
      </div>

      <div className="row">
        <div className="tabs-container">
          <div className="tab-item">
            <div className="tab-title">Online Vehicles</div>
            <div className="tab-content">
              <div className="left">
                <div className="tab-value">
                  {dashboardData?.online_vehicles || 0}
                </div>
                <div className="tab-tag">
                  <TrendTag trend={dashboardData?.online_vehicles_change} />
                </div>
              </div>
              <div className="right">
                <img src={carOnline} alt="car image" />
              </div>
            </div>
          </div>

          <div className="tab-item">
            <div className="tab-title">Offline Vehicles</div>
            <div className="tab-content">
              <div className="left">
                <div className="tab-value">
                  {dashboardData?.offline_vehicles || 0}
                </div>
                <div className="tab-tag">
                  <TrendTag trend={dashboardData?.offline_vehicles_change} />
                </div>
              </div>
              <div className="right">
                <img src={carOffline} alt="car image" />
              </div>
            </div>
          </div>

          <div className="tab-item">
            <div className="tab-title">Suspended Vehicles</div>
            <div className="tab-content">
              <div className="left">
                <div className="tab-value">
                  {dashboardData?.suspended_vehicles || 0}
                </div>
                <div className="tab-tag">
                  <TrendTag trend={dashboardData?.suspended_vehicles_change} />
                </div>
              </div>
              <div className="right">
                <img src={carSuspended} alt="car image" />
              </div>
            </div>
          </div>

          <div className="tab-item">
            <div className="tab-title">High Risk Activity</div>
            <div className="tab-content">
              <div className="left">
                <div className="tab-value">
                  {dashboardData?.high_risk_vehicles || 0}
                </div>
                <div className="tab-tag">
                  <TrendTag trend={dashboardData?.high_risk_vehicles_change} />
                </div>
              </div>
              <div className="right">
                <img src={alarm} alt="car image" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div style={{ flex: 3 }} className="container">
          <div style={{ position: "relative" }} className="container-head">
            <div className="title">Annual Theft Details</div>
            <span className="graph-value">
              {annualTheftCount !== null
                ? annualTheftCount
                : dashboardData?.annual_theft_count || 0}
            </span>
            <div className="select">
              <DatePicker
                picker="year"
                style={{ width: "250px", height: "38px" }}
                placeholder="Select Year"
                value={selectedYear}
                onChange={handleYearChange}
              />
            </div>
          </div>

          {graphLoading ? (
            <div className="empty-state">Loading updated chart data...</div>
          ) : hasGraphData ? (
            <StackedBarChart graphData={graphData} categoryKey="month" />
          ) : (
            <div
              className="empty-state"
              style={{ textAlign: "center", padding: "40px", color: "#999" }}
            >
              <Empty description="No theft data available for selected year" />
            </div>
          )}
        </div>

        <div style={{ flex: 1 }} className="container">
          <div className="container-head">
            <div className="title">Recent Interactions</div>
          </div>
          <div className="customers-container">
            {dashboardData?.recent_interactions?.length > 0 ? (
              dashboardData?.recent_interactions?.map((customer) => {
                const isAnswered = customer.status === "answered";

                return (
                  <div
                    key={customer?.id}
                    onClick={() => handleCustomerClick(customer)}
                  >
                    <div className="customer">
                      <div className="customer-container">
                        <div className="dp">
                          <img
                            src={
                              customer?.profile_picture
                                ? `${process.env.REACT_APP_MEDIA_URL}${customer?.profile_picture}`
                                : customerLogo
                            }
                            alt="dp"
                          />
                        </div>
                        <div>
                          <div className="name">{customer?.customer_name}</div>
                          <div
                            className={`reaction ${isAnswered ? "Answered" : "NotAnswered"
                              }`}
                          >
                            {isAnswered ? "Answered" : "Not Answered"}
                          </div>
                        </div>
                      </div>

                      <Tooltip
                        title={
                          !customer?.score
                            ? "An error occurred while initiating this call"
                            : ""
                        }
                        placement="top"
                      >
                        <div
                          style={{
                            backgroundColor: getStrokeColor(customer?.score),
                            cursor: !customer?.score ? "pointer" : "default",
                          }}
                          className="score"
                        >
                          {customer?.score || "N/A"}
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-state">
                <Empty description="No interactions to show" />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="container">
          <div className="container-head timeline-header">
            <div className="title">Automated Shutdown Events</div>
            <div className="select">
              <Select
                mode="multiple"
                allowClear
                style={{ width: "350px" }}
                placeholder="Select Vehicles"
                value={selectedVehicles}
                onChange={setSelectedVehicles}
                maxTagCount="responsive"
              >
                {vehicleOptions.map((option) => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>
          {formattedTimelineData.length > 0 ? (
            <TimelineGraph
              filteredData={formattedTimelineData}
              vehicleOptions={vehicleOptions}
              compressed={isCompressed}
            />
          ) : (
            <div className="empty-state">
              <Empty description="No automated shutdown events to show" />
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="container">
          <div className="container-head">
            <div className="title">Vehicles Heat Map</div>
          </div>
          <HeatMapComponent data={heatmapData} />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
